import { useState } from 'react';
import { Eye, Loader2, X } from 'lucide-react';

const LABELS = {
  career_direction: 'Career Direction',
  profile_strength: 'Profile Strength',
  interview_readiness: 'Interview Readiness',
  job_search: 'Job Search Readiness',
  all_strong: 'Strong across all areas',
};

const SampleCard = ({ sample }) => (
  <div data-testid={`v2-sample-${sample.profile}`} className="border border-slate-200 rounded-xl overflow-hidden">
    <div className="p-3 bg-slate-50 border-b border-slate-200 text-sm space-y-1">
      <div className="flex flex-wrap gap-2 items-center">
        <span className="font-semibold text-slate-800">{LABELS[sample.profile]}</span>
        <span className="px-2 py-0.5 rounded bg-white border text-xs">{sample.student.firstName} · {sample.student.stage} · {sample.student.qualification}</span>
        <span className="px-2 py-0.5 rounded bg-white border text-xs">{sample.word_count} words</span>
        <span className="px-2 py-0.5 rounded bg-white border text-xs">{sample.generated_by === 'ai' ? 'AI' : 'Template'}</span>
      </div>
      <p className="text-slate-600"><b>Subject:</b> {sample.subject}</p>
    </div>
    <iframe title={sample.profile} srcDoc={sample.preview_html || sample.body_html} className="w-full h-[640px] bg-white" />
  </div>
);

export const FollowupV2Preview = ({ authFetch, apiUrl }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [samples, setSamples] = useState([]);
  const [error, setError] = useState('');

  const load = async () => {
    setOpen(true); setLoading(true); setError('');
    setSamples([]);
    try {
      const res = await authFetch(`${apiUrl}/api/admin/followups/preview-samples`);
      if (!res.ok) throw new Error('Failed to load sample profiles');
      const { profiles } = await res.json();
      await Promise.all(profiles.map(async (p) => {
        const r = await authFetch(`${apiUrl}/api/admin/followups/preview-samples/${p}`);
        if (!r.ok) throw new Error(`Failed to generate ${LABELS[p]} sample`);
        const sample = await r.json();
        setSamples((prev) => [...prev, sample].sort((a, b) => profiles.indexOf(a.profile) - profiles.indexOf(b.profile)));
      }));
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  };

  return (
    <>
      <button data-testid="preview-v2-samples-btn" onClick={load}
        className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg border border-teal-200 text-teal-700 hover:bg-teal-50 transition-colors">
        <Eye className="w-4 h-4" /> Preview v2 samples
      </button>
      {open && (
        <div data-testid="v2-samples-modal" className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-5 space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="font-semibold text-slate-900">v2 template samples (not sent)</h3>
              <button data-testid="v2-samples-close-btn" onClick={() => setOpen(false)}><X className="w-5 h-5" /></button>
            </div>
            {loading && <p className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Generating 5 sample emails (about 30-60s)...</p>}
            {error && <p data-testid="v2-samples-error" className="text-sm text-red-600">{error}</p>}
            {samples.map((s) => <SampleCard key={s.profile} sample={s} />)}
          </div>
        </div>
      )}
    </>
  );
};
